import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { Store } from '@ngrx/store';

import { LazyLoadLegacyStore } from './lazy-load-legacy-store';
import { APPS_ENABLED, LOAD_LEGACY } from './lazy-load-legacy.constants';
import { Applicative } from './lazy-load-legacy.model';

import { CommonWindow } from '@core/domain/models/common-window.model';
import { ConfigService } from '@core/services/config/config.service';
import { EnvironmentService } from '@core/services/environment.service';
import { TimestampService } from '@core/services/timestamp.service';
import { LazyLoadService } from '@shared/services/lazy-load.service';

declare var angular;

@Injectable()

export class LazyLoadLegacyService {
    private readonly appsDependencyMap = {
        CMS: {
            scripts: [],
            styles: [],
            order: 1,
        },
        legacy: {
            scripts: [],
            styles: ['css/legacy.css'],
            order: 2,
        },
    };

    constructor(
        private readonly lazyLoadLegacyStoreService: LazyLoadLegacyStore,
        private readonly configService: ConfigService,
        private readonly environmentService: EnvironmentService,
        private readonly lazyLoadService: LazyLoadService,
        private readonly timestampService: TimestampService,
        private readonly store: Store,
        @Inject(DOCUMENT) private readonly document: Document,
        @Inject(WINDOW) private readonly window: CommonWindow,
        @Inject(APPS_ENABLED) private readonly appsEnabled: Applicative[] | Applicative[][] = [],
        @Inject(LOAD_LEGACY) private readonly loadLegacyEnabled: boolean[] | boolean = [false],
    ) {
        // TODO ECOMDUTI-20994 [TechDebt] - Eliminar este flag cuando se haya finalizado la migración de legacy
        const hybridPage: boolean = !!this.window.inditex?.hybridPage;

        this.loadLegacyEnabled = Array.isArray(
            this.loadLegacyEnabled
        ) ? this.loadLegacyEnabled.some((x) => x) : this.loadLegacyEnabled;

        if (hybridPage || this.window.inditex?.legacyLoaded) {
            this.loadLegacyEnabled = false;
        }

        this.appsEnabled = (this.appsEnabled as any).reduce((acc, curr) => {
            curr.forEach((appName) => {
                if (!acc.includes(appName)) {
                    acc.push(appName);
                }
            });

            return acc;
        }, []);
    }

    public init(): Promise<boolean | undefined> {
        // const shouldInitialize = !this.lazyLoadLegacyStoreService.loaded.value
        // && !this.lazyLoadLegacyStoreService.loading
        // && this.loadLegacyEnabled;

        // if (shouldInitialize) {
        //     this.lazyLoadLegacyStoreService.loading = true;
        //     delay(2500);
        //     return this.loadScripts()
        //         .then(() => this.loadStyles())
        //         .then(() => this.bootstrapAngularJS())
        //         .then(() => {
        //             this.lazyLoadLegacyStoreService.loaded.next(true);
        //             this.lazyLoadLegacyStoreService.loading = false;
        //             if (this.window.inditex) {
        //                 this.window.inditex.legacyLoaded = true;
        //             }
        //             return true;
        //         })
        //         .catch(() => {
        //             this.lazyLoadLegacyStoreService.loaded.next(false);
        //             this.lazyLoadLegacyStoreService.loading = false;
        //             return false;
        //         });
        // }
        return Promise.resolve(true);
    }

    public loadAvailabilityInStoreLegacy(): Promise<boolean | undefined> {
        // eslint-disable-next-line rxjs/no-subject-value
        const shouldInitialize = !this.lazyLoadLegacyStoreService.loaded.value
        && !this.lazyLoadLegacyStoreService.loading
        && this.loadLegacyEnabled;

        if (shouldInitialize) {
            this.lazyLoadLegacyStoreService.loading = true;
            const legacyBaseUrls = this.configService.getConfigUrl<Record<string, string>>('legacyBaseUrls');
            const staticContentBaseUrl = legacyBaseUrls[this.environmentService.getEnvironment()];
            const path = 'js/legacy-reduce.min.js';
            const promises = [
                this.lazyLoadService.loadScript(`${staticContentBaseUrl}${path}?t=${this.timestampService.getTs()}`),
            ];
            return Promise.all(promises)
                .then(this.loadStyles.bind(this))
                .then(() => this.bootstrapAngularJS())
                .then(() => {
                    this.lazyLoadLegacyStoreService.loaded.next(true);
                    this.lazyLoadLegacyStoreService.loading = false;
                    if (this.window.inditex) {
                        this.window.inditex.legacyLoaded = true;
                    }
                    return true;
                })
                .catch((_error) => {
                    this.lazyLoadLegacyStoreService.loaded.next(false);
                    this.lazyLoadLegacyStoreService.loading = false;
                    return false;
                });
        }
        return Promise.resolve(true);
    }

    private loadScripts() {
        const scriptPaths = (this.appsEnabled as any)
            .sort((a, b) => {
                return this.appsDependencyMap[a].order < this.appsDependencyMap[b].order ? -1 : 0;
            })
            .reduce((acc, curr) => {
                return acc.concat(this.appsDependencyMap[curr].scripts);
            }, []);

        const legacyBaseUrls = this.configService.getConfigUrl<Record<string, string>>('legacyBaseUrls');

        const staticContentBaseUrl = legacyBaseUrls[this.environmentService.getEnvironment()];

        const promises = scriptPaths
            .map((path) => this.lazyLoadService.loadScript(`${staticContentBaseUrl}${path}?t=${this.timestampService.getTs()}`));

        return Promise.all(promises);
    }

    private loadStyles() {
        const stylePaths = (this.appsEnabled as any).reduce((acc, curr) => {
            return acc.concat(this.appsDependencyMap[curr].styles);
        }, []);

        const legacyBaseUrls = this.configService.getConfigUrl<Record<string, string>>('legacyBaseUrls');

        const staticContentBaseUrl = legacyBaseUrls[this.environmentService.getEnvironment()];

        const promises = stylePaths.map((path) => this.lazyLoadService.loadStyle(`${staticContentBaseUrl}${path}?t=${this.timestampService.getTs()}`));

        return Promise.all(promises);
    }

    private getEventData(event) {
        if (event?.detail?.extra?.type) {
            return event.detail.extra;
        }
        return false;
    }

    private handleError(detailError?) {
        console.error(`LazyLoadLegacyService - LEGACY EVENTS ERROR: ${detailError}`);
    }

    private handleEvent(event) {
        const data = this.getEventData(event);

        const $injector = angular.element('body').injector();

        if (data && $injector.has('CmsPageService')) {
            const CmsPageService = $injector.get('CmsPageService');
            CmsPageService.handleClick(data);
        } else {
            this.handleError();
        }
    }

    private bootstrapAngularJS() {
        // TODO ECOMDUTI-20994 Si el pais tiene activo el login nuevo esto no se carga...
        const mainAngularJs = this.document.createElement('transversal-elements');

        this.document.body.appendChild(mainAngularJs);

        this.document.addEventListener('legacy-event', this.handleEvent.bind(this));

        angular.bootstrap(this.document, ['itxApp.legacy']);

        return Promise.resolve(true);
    }
}
